import classNames from 'classnames/bind';
import styles from './EmployeeGrid.module.scss';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FaLinkedin } from 'react-icons/fa';
import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { getEmployeePhotoSrc } from '../../../lib/employee-photo-src';
import {
  getEmployeeProfilePath,
  hasAnsattLandingContent,
} from '../../../lib/employee-href';
import { getLanguageFromPath } from '../../../lib/polylang';
import { getSiteUi } from '../../../lib/site-ui';
import { useIsMobile } from '../../../hooks';
import 'swiper/css';
import 'swiper/css/pagination';

let cx = classNames.bind(styles);

export default function EmployeeGrid({ data, noWrapper = false }) {
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();
  const router = useRouter();
  const languageCode = getLanguageFromPath(router?.asPath || '/').code;
  const readMoreAboutMe = getSiteUi(languageCode).employeeLanding.readMoreAboutMe;

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!data?.nodes?.length) return null;

  const sortedEmployees = [...data.nodes].sort((a, b) => {
    const aOrder = a.ansatte?.rekkefolge || 0;
    const bOrder = b.ansatte?.rekkefolge || 0;
    return aOrder - bOrder;
  });

  const extractEmail = (wysiwygContent) => {
    if (!wysiwygContent) return null;
    const mailtoMatch = wysiwygContent.match(/href=["']mailto:([^"']+)["']/);
    if (mailtoMatch) return mailtoMatch[1];
    const emailRegex = /[\w._%+-]+@[\w.-]+\.[a-zA-Z]{2,}/;
    const match = wysiwygContent.match(emailRegex);
    return match ? match[0] : null;
  };

  const extractUrl = (wysiwygContent) => {
    if (!wysiwygContent) return null;
    const hrefMatch = wysiwygContent.match(/href=["'](https?:\/\/[^"']+)["']/);
    if (hrefMatch) return hrefMatch[1];
    const urlRegex = /(https?:\/\/[^\s<>"']+)/;
    const match = wysiwygContent.match(urlRegex);
    if (match) return match[0].replace(/[<>]/g, '');
    const linkedinRegex = /(?:www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+/;
    const linkedinMatch = wysiwygContent.match(linkedinRegex);
    if (linkedinMatch) {
      return linkedinMatch[0].startsWith('http') ? linkedinMatch[0] : `https://${linkedinMatch[0]}`;
    }
    return null;
  };

  const renderEmployeeCard = (employee, index) => {
    const emailAddress = extractEmail(employee.ansatte?.email);
    const linkedinUrl = extractUrl(employee.ansatte?.linkedin);
    const altText = employee.ansatte?.employeePhoto?.node?.altText || employee.title || 'Employee photo';
    const isPriority = index < 4;
    const profileHref = employee.slug
      ? getEmployeeProfilePath(employee, languageCode)
      : null;
    const showReadMore = hasAnsattLandingContent(employee);

    return (
      <div className={cx('employeeCard')}>
        {profileHref && (
          <Link href={profileHref} className={cx('profileLink')} aria-label={employee.title} />
        )}
        {employee.ansatte?.employeePhoto?.node && (
          <div className={cx('imageWrapper')}>
            <Image
              src={getEmployeePhotoSrc(employee.ansatte.employeePhoto.node, 400)}
              alt={altText}
              width={400}
              height={500}
              className={cx('image')}
              priority={isPriority}
              loading={isPriority ? 'eager' : 'lazy'}
              sizes="(max-width: 480px) 300px, (max-width: 768px) 350px, (max-width: 1024px) 300px, 400px"
              quality={75}
              placeholder="blur"
              blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNTAwIiBmaWxsPSIjZWVlIi8+PC9zdmc+"
            />
          </div>
        )}
        <div className={cx('info')}>
          <h3>{employee.title}</h3>
          <p className={cx('role')}>{employee.ansatte.role}</p>

          {emailAddress && (
            <a href={`mailto:${emailAddress}`} className={cx('email')} onClick={(e) => e.stopPropagation()}>
              {emailAddress}
            </a>
          )}

          {linkedinUrl && (
            <a
              href={linkedinUrl}
              className={cx('linkedin')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <FaLinkedin /> LinkedIn
            </a>
          )}

          {showReadMore && (
            <span className={cx('readMore')} aria-hidden>
              {readMoreAboutMe}
            </span>
          )}
        </div>
      </div>
    );
  };

  const showCarousel = mounted && isMobile;

  const employeeGrid = (
    <>
      <div className={cx('grid', { gridHidden: showCarousel })}>
        {sortedEmployees.map((employee, index) => (
          <React.Fragment key={employee.id || index}>
            {renderEmployeeCard(employee, index)}
          </React.Fragment>
        ))}
      </div>

      {showCarousel && (
        <div className={cx('carousel')}>
          <Swiper
            modules={[Pagination]}
            slidesPerView={1}
            spaceBetween={16}
            pagination={{ clickable: true }}
            className={cx('swiper')}
            grabCursor
          >
            {sortedEmployees.map((employee, index) => (
              <SwiperSlide key={employee.id || index} className={cx('slide')}>
                {renderEmployeeCard(employee, index)}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </>
  );

  if (noWrapper) {
    return employeeGrid;
  }

  return (
    <section className={cx('employeeSection')}>
      <div className={cx('container')}>
        {employeeGrid}
      </div>
    </section>
  );
}
